import { Injector, Injectable } from '@angular/core';
import { FilterableService } from '../../../../filterable-service';
import { WorkOrderService, WorkOrderNotificationService } from '../../../../ve.service';
import { WorkOrdersService } from '../../../work-orders.service';
import { gaWoSubmitReportForm } from '../../ga-work-orders.template';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

const FORM_CONTROLS = [
    'ActualStartDate',
    'ActualCompletionDate',
    'StatusId',
    'HoursToComplete',
    'HoursToSyncMath',
    'LoadClearanceIssues',
    'LoadInterferenceIssues',
    'NumOfMathIssues',
    'PTAClearanceViolations',
    'PTAIntereferenceIssues'
];

export enum Statuses {
    Assigned = 'Assigned',
    InProgress = 'InProgress',
    Complete = 'Complete',
    OnHold = 'OnHold',
    Cancelled = 'Cancelled'
}

@Injectable()
export class ReportsService {
    formBuilder: UntypedFormBuilder;
    editForm: UntypedFormGroup;
    service: WorkOrdersService;
    workOrderService: WorkOrderService;
    woNotificationService: WorkOrderNotificationService;
    statusList = new FilterableService();
    contactsList = new FilterableService();
    status: any;
    date: Date;
    today: Date;
    defaultContacts: any;
    min = 0;
    decimals = 0;
    format = 'n0';
    originalWoStatusId: any;

    constructor(injector: Injector) {
        this.formBuilder = injector.get(UntypedFormBuilder);
        this.service = injector.get(WorkOrdersService);
        this.workOrderService = injector.get(WorkOrderService);
        this.woNotificationService = injector.get(WorkOrderNotificationService);
        this.editForm = this.formBuilder.group(gaWoSubmitReportForm);
    }

    getStatuses(status: any): Promise<any> {
        return this.workOrderService.read('/GetWorkOrderStatuses')
            .then(response => {
                const oldStatus = response.find(x => x.Id === this.originalWoStatusId);
                this.editForm.get('StatusId').setValue(response.filter(item => item.Description === status)[0].Id);
                this.editForm.get('Status').setValue(status);

                response = response
                    .filter(item => item.Description === Statuses.Assigned
                        || item.Description === Statuses.InProgress
                        || item.Description === Statuses.Complete
                        || item.Description === Statuses.OnHold
                        || item.Description === Statuses.Cancelled)
                    .sort((a, b) => a.Description.localeCompare(b.Description));

                if (this.status !== 'Assigned' && oldStatus.Description !== 'Complete') {
                     const filteredStatuses = response.filter(x => x.Description !== 'Assigned');
                    this.statusList.next(filteredStatuses);
                } else if (oldStatus.Description === 'Complete' && this.status === 'Complete') {
                    this.statusList.next(response.filter(x => {
                        if (x.Description === 'Complete') {
                            return x;
                        }
                    }));
                } else {
                    this.statusList.next(response);
                }
            });
    }

    getDefaultContacts(): Promise<any> {
        return this.woNotificationService.read('/Module/VE_GA')
            .then(response => {
                if (response) {
                    response.forEach(element => {
                        if (element.WorkOrderStatusDescription === 'Complete') {
                            const contacts = Object.assign([], element.ContactList);
                            this.defaultContacts = contacts;
                        }
                    });
                }
            });
    }

    populateContactsList(): Promise<any> {
        return this.woNotificationService.read('/ContactsForModule/VE_GA')
            .then(response => {
                this.contactsList.next(response);
            });
    }

    onStatusChange(event: any) {
        const statusId = event;
        this.editForm.get('StatusId').setValue(statusId);
        this.editForm.get('EmailNotes').enable();
        this.workOrderService.read('/GetWorkOrderStatuses')
            .then(response => {
                if (statusId) {
                    this.status = response.filter(item => item.Id === statusId)[0].Description;
                    if (this.status !== 'Assigned') {
                        const filteredStatuses = response.filter(x => x.Description !== 'Assigned');
                        this.statusList.next(filteredStatuses);
                    }
                    this.editForm.get('Status').setValue(this.status);
                    this.setReportsFormValidators(this.status);
                }
            });
    }

    // TODO: disable EmailNotes if no contacts AND additional emails are specified
    onContactsChange(event: any) {
        const contacts = event;
        const emails = this.formatEmails(this.editForm.get('AdditionalEmailIds').value);
        const validContacts: boolean = contacts && (contacts.length > 0);
        const vaildEmails: boolean = emails && (emails !== '');

        if (validContacts || vaildEmails) {
            this.editForm.get('EmailNotes').enable();
        } else {
            this.editForm.get('EmailNotes').setValue('');
            this.editForm.get('EmailNotes').disable();
        }
    }

    isSubmitDisabled(): boolean | undefined {
        const statusIdControl = this.editForm.get('StatusId');
        if (this.originalWoStatusId === statusIdControl.value) {
            return true;
        } else if (this.status === Statuses.InProgress || this.status === Statuses.Complete) {
            return this.editForm.invalid;
        }
    }

    setReportsFormValidators(status: any) {
        this.removeValidators();
        this.editForm.get('StatusId').setValidators([Validators.required]);

        if (status === Statuses.InProgress) {
            this.editForm.get('ActualStartDate').setValidators([Validators.required]);
            this.editForm.get('ActualStartDate').updateValueAndValidity();
        } else if (status === Statuses.Complete) {
            FORM_CONTROLS.forEach(control => {
                this.editForm.get(control).setValidators([Validators.required]);
                this.editForm.get(control).updateValueAndValidity();
            });
        }
    }

    private removeValidators() {
        this.editForm.get('ActualStartDate').clearValidators();
        this.editForm.get('ActualStartDate').updateValueAndValidity();

        FORM_CONTROLS.forEach(control => {
            this.editForm.get(control).clearValidators();
            this.editForm.get(control).updateValueAndValidity();
        });
    }

    /**
     * Convert string of emails to list of emails
     * @param otherEmails
     */
    formatEmails(otherEmails: any): string {
        if (otherEmails) {
            const emails: any[] = [];
            otherEmails.split('.com').forEach(element => {
                const x = element.replace(/[, ]+/g, ' ').trim();
                if (x && x !== '') {
                    emails.push(x + '.com');
                }
            });
            return emails.join();
        }

        return null;
    }
}
